import io, { Socket } from 'socket.io-client';

export type SocketOptions = {
  url: string;
  onDisconnect: () => void;
  timeout: number;
};

export type SocketQuery = {
  [key: string]: string;
};

export type OnConnectFunc = () => void;
type emitParams = Parameters<Socket['emit']>;

/**
 * Events dropped rather than queued while the socket is down.
 *
 * An ICE candidate belongs to the peer session that was live when it was
 * generated. Reconnecting does not carry that session over — the far side is
 * gone by the time the queue flushes — so replaying candidates delivers
 * nothing usable. It does deliver them all in a single tick, which is enough
 * on its own to exhaust the server's per-minute signaling budget and start
 * refusing the fresh candidates that would have rebuilt the connection.
 */
const UNREPLAYABLE_EVENTS = new Set(['webrtc-signal']);

export class SocketInstance {
  private active = true;

  /**
   * True between an intentional hop's disconnect and the connection that
   * replaces it. That drop must not start the countdown: the user would get the
   * "Connection problems" dialog on top of the placement overlay for a hop that
   * is working normally. A hop that never lands is caught by placement's own
   * timeout instead.
   */
  private isHopping = false;
  private readonly url: string;
  private readonly onDisconnect: () => void;
  private readonly timeout: number;
  private timerId?: NodeJS.Timeout;

  private onConnectOnce: OnConnectFunc[];

  public readonly socket: Socket;
  public isConnected = false;

  constructor(options: SocketOptions) {
    this.onDisconnect = options.onDisconnect;
    this.url = options.url;
    this.timeout = options.timeout;
    this.socket = io(this.url, {
      transports: ['websocket'],
      autoConnect: false,
      withCredentials: true,
    });

    const socketEmit = this.socket.emit.bind(this.socket);
    this.socket.emit = (ev: emitParams[0], ...args: emitParams[1]) => {
      if (!this.isConnected && UNREPLAYABLE_EVENTS.has(String(ev))) {
        return this.socket;
      }
      this.callIfConnected(socketEmit.bind(this.socket, ev, ...args));
      return this.socket;
    };

    this.onConnectOnce = [];

    this.socket.on('disconnect', () => {
      this.isConnected = false;
      if (this.active && !this.isHopping) {
        this.startCountdown();
      }
    });
  }

  public callIfConnected(func: OnConnectFunc) {
    if (this.isConnected) {
      func();
      return;
    }

    this.onConnectOnce.push(func);
  }

  private startCountdown() {
    if (this.timerId) {
      clearTimeout(this.timerId);
    }

    this.timerId = setTimeout(() => {
      if (!this.isConnected) {
        this.onDisconnect();
      }
    }, this.timeout);
  }

  public disconnect() {
    this.active = false;
    this.socket.disconnect();
    if (this.timerId) {
      clearTimeout(this.timerId);
    }
  }

  public updateQuery(query: SocketQuery) {
    this.socket.io.opts.query = query;
  }

  public connect(query: SocketQuery) {
    // disconnect() parks the instance by clearing `active`; a fresh connect
    // revives it, otherwise a later drop would never start the countdown and
    // the connection-problem dialog would stay silent for the rest of the
    // session.
    this.active = true;
    this.isHopping = false;
    this.startCountdown();
    this.updateQuery(query);
    this.socket.connect();
  }

  /**
   * Drops the connection and immediately opens a new one, keeping the current
   * handshake query — used when the server asks the client to land on a
   * different instance. The query carries the user's token, so it must survive
   * the hop: reconnecting with a blank one would mint a new guest identity
   * mid-flow.
   *
   * Emits issued during the gap are queued by `emit` and flush once the new
   * connection reports itself connected.
   */
  public reconnect() {
    this.isHopping = true;
    this.isConnected = false;
    this.socket.disconnect();
    this.socket.connect();
  }

  public setIsConnected(value: boolean) {
    this.isConnected = value;

    if (value) {
      this.isHopping = false;

      this.onConnectOnce.forEach((func) => {
        func();
      });

      this.onConnectOnce = [];
    }
  }
}
